import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { message } from 'antd';
import { useAuthStore } from '../store/authStore';

// 重启模式标记：为 true 时抑制全局错误弹窗
let _serverRestarting = false;
export const setServerRestarting = (v: boolean) => {
  _serverRestarting = v;
};
export const isServerRestarting = () => _serverRestarting;

// 根据环境变量确定API地址
const getBaseURL = () => {
  // 如果有环境变量，使用环境变量
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // 开发环境使用相对路径（通过Vite代理）
  if (import.meta.env.DEV) {
    return '/api/v1';
  }

  // 生产环境使用相对路径（通过nginx代理）
  return '/api/v1';
};

// 创建axios实例
const api: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 响应拦截器
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // 如果响应包含标准API格式 {code, message, data}，则返回data字段
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data;
    }
    // 否则返回原始响应数据
    return response.data;
  },
  error => {
    const { response } = error;

    // 重启模式下抑制所有错误弹窗
    if (_serverRestarting) {
      return Promise.reject(error);
    }

    if (response?.status === 401) {
      // 未授权，清除登录状态
      useAuthStore.getState().clearAuth();
      message.error('登录已过期，请重新登录');
    } else if (response?.status === 403) {
      message.error('权限不足');
    } else if (response?.status === 500) {
      message.error('服务器错误，请稍后重试');
    } else if (error.code === 'ECONNABORTED') {
      message.error('请求超时，请检查网络连接');
    } else {
      message.error(response?.data?.detail || response?.data?.message || '网络错误，请稍后重试');
    }

    return Promise.reject(error);
  }
);

// API接口定义
export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface PaginationResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AiSupportConfig {
  id?: number;
  enabled: boolean;
  provider: string;
  api_key?: string;
  api_key_configured?: boolean;
  api_key_masked?: string | null;
  model: string;
  base_url?: string | null;
  request_timeout: number;
  low_risk_auto_reply_enabled: boolean;
  allowed_auto_reply_intents: string[];
  force_handoff_keywords: string[];
  last_tested_at?: string | null;
}

export interface KbDocument {
  id: number;
  type: 'qa' | 'article';
  title: string;
  question?: string | null;
  answer?: string | null;
  content?: string | null;
  tags: string[];
  priority: number;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  chunk_count?: number;
}

export interface AiPreviewResult {
  intent: string;
  risk_level: 'low' | 'medium' | 'high';
  can_answer_from_kb: boolean;
  reply_text: string;
  handoff_reason: string;
  matched_document_ids: number[];
}

export interface AiReplyLog {
  id: number;
  bot_id?: number | null;
  customer_telegram_id?: number | null;
  source_message_id?: number | null;
  request_text: string;
  decision: string;
  intent?: string | null;
  risk_level?: string | null;
  matched_document_ids: number[];
  reply_text?: string | null;
  handoff_reason?: string | null;
  sent_message_id?: number | null;
  created_at?: string | null;
}

// 认证相关接口
export const authApi = {
  login: (data: { username: string; password: string }) =>
    api.post<ApiResponse<{ user: any; token: string }>>('/auth/login', data),

  logout: () => api.post('/auth/logout'),

  refreshToken: () => api.post<ApiResponse<{ token: string }>>('/auth/refresh'),

  getUserInfo: () => api.get<ApiResponse<any>>('/auth/me'),
};

// 仪表板数据接口
export const dashboardApi = {
  getStats: () => api.get<ApiResponse<any>>('/analytics/dashboard'),

  getRecentOrders: (params?: { limit?: number }) =>
    api.get<ApiResponse<any[]>>('/analytics/recent-orders', { params }),

  getRevenueChart: (days: number) =>
    api.get<ApiResponse<any>>('/analytics/sales', { params: { days } }),

  getUserGrowth: (days: number) =>
    api.get<ApiResponse<any>>('/analytics/users/growth', { params: { days } }),
};

// 商品管理接口
export const productApi = {
  getProducts: (params?: PaginationParams & { search?: string; bot_id?: number }) =>
    api.get<ApiResponse<PaginationResponse<any>>>('/products', { params }),

  getProduct: (id: number) => api.get<ApiResponse<any>>(`/products/${id}`),

  createProduct: (data: any) => api.post<ApiResponse<any>>('/products', data),

  updateProduct: (id: number, data: any) => api.put<ApiResponse<any>>(`/products/${id}`, data),

  deleteProduct: (id: number) => api.delete<ApiResponse<any>>(`/products/${id}`),

  batchDeleteProducts: (ids: number[]) =>
    api.post<ApiResponse<any>>('/products/batch-delete', { ids }),

  toggleProductStatus: (id: number) => api.patch<ApiResponse<any>>(`/products/${id}/toggle`),
};

// 群组管理接口
export const groupApi = {
  getGroups: (
    params?: PaginationParams & {
      search?: string;
      bot_id?: number;
      approval_status?: string;
      sort_by?: 'created_at';
      sort_order?: 'asc' | 'desc';
    }
  ) => api.get<ApiResponse<PaginationResponse<any>>>('/groups', { params }),

  getGroup: (id: number) => api.get<ApiResponse<any>>(`/groups/${id}`),

  createGroup: (data: any) => api.post<ApiResponse<any>>('/groups', data),

  updateGroup: (id: number, data: any) => api.put<ApiResponse<any>>(`/groups/${id}`, data),

  deleteGroup: (id: number) => api.delete<ApiResponse<any>>(`/groups/${id}`),

  generateInviteLink: (id: number) =>
    api.post<ApiResponse<{ link: string }>>(`/groups/${id}/invite`),

  getGroupMembers: (id: number, params?: PaginationParams) =>
    api.get<ApiResponse<PaginationResponse<any>>>(`/groups/${id}/members`, { params }),

  // 审批相关接口
  getPendingGroups: (params?: PaginationParams) =>
    api.get<ApiResponse<PaginationResponse<any>>>('/groups/pending-approval', { params }),

  approveGroup: (id: number) => api.post<ApiResponse<any>>(`/groups/${id}/approve`),

  rejectGroup: (id: number, reason?: string) =>
    api.post<ApiResponse<any>>(`/groups/${id}/reject`, { reason }),

  // 审批记录接口
  getApprovalLogs: (params?: PaginationParams & { action?: string; source?: string }) =>
    api.get<ApiResponse<PaginationResponse<any>>>('/groups/approval-logs', { params }),

  getApprovalLogsStats: () => api.get<ApiResponse<any>>('/groups/approval-logs/stats'),

  // 自动审批配置
  getAutoApprove: () => api.get<ApiResponse<{ enabled: boolean }>>('/groups/auto-approve'),

  setAutoApprove: (enabled: boolean) =>
    api.post<ApiResponse<{ enabled: boolean }>>('/groups/auto-approve', { enabled }),
};

// 用户管理接口
export const userApi = {
  getUsers: (
    params?: PaginationParams & {
      search?: string;
      status?: string;
      sort_by?: 'join_date' | 'bots_count' | 'total_earnings';
      sort_order?: 'asc' | 'desc';
      role?: string;
    }
  ) => api.get<ApiResponse<PaginationResponse<any>>>('/users', { params }),

  getUser: (id: number) => api.get<ApiResponse<any>>(`/users/${id}`),

  updateUser: (id: number, data: any) => api.put<ApiResponse<any>>(`/users/${id}`, data),

  banUser: (id: number) => api.patch<ApiResponse<any>>(`/users/${id}/ban`),

  unbanUser: (id: number) => api.patch<ApiResponse<any>>(`/users/${id}/unban`),

  getUserMemberships: (id: number) => api.get<ApiResponse<any[]>>(`/users/${id}/memberships`),

  getUserBots: (id: number) => api.get<ApiResponse<any[]>>(`/users/${id}/bots`),

  getUserActivity: (id: number, params?: { limit?: number }) =>
    api.get<ApiResponse<any>>(`/users/${id}/activity`, { params }),

  getUserStats: (id: number) => api.get<ApiResponse<any>>(`/users/${id}/stats`),

  adjustBalance: (id: number, data: { amount: number; reason: string }) =>
    api.post<ApiResponse<any>>(`/users/${id}/adjust-balance`, data),

  generateRecoveryKey: (id: number) =>
    api.post<ApiResponse<any>>(`/users/${id}/generate-recovery-key`),

  // 2026-10-14 安全加固：恢复密钥不再随用户列表/详情返回，改为按需单独拉取（单个用户）
  getRecoveryKey: (id: number) =>
    api.get<ApiResponse<any>>(`/users/${id}/recovery-key`),
};

// 代理中心接口
export const agentCenterApi = {
  getOverview: (params?: { page?: number; page_size?: number }) =>
    api.get<ApiResponse<any>>('/agents/overview', { params }),

  getDetail: (
    agentUserId: number,
    params?: { turnover_page?: number; turnover_page_size?: number }
  ) => api.get<ApiResponse<any>>(`/agents/${agentUserId}`, { params }),

  createAgent: (data: { identifier: string }) => api.post<ApiResponse<any>>('/agents', data),

  bindMember: (agentUserId: number, data: { identifier: string }) =>
    api.post<ApiResponse<any>>(`/agents/${agentUserId}/bindings`, data),

  unbindMember: (agentUserId: number, memberUserId: number) =>
    api.delete<ApiResponse<any>>(`/agents/${agentUserId}/bindings/${memberUserId}`),

  deleteAgent: (agentUserId: number) => api.delete<ApiResponse<any>>(`/agents/${agentUserId}`),
};

// 分销中心接口
export const distributionAdminApi = {
  getOverview: () => api.get<ApiResponse<any>>('/distribution-admin/overview'),

  getProducts: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    sort_by?: 'created_at' | 'name' | 'current_rate';
    sort_order?: 'asc' | 'desc';
  }) => api.get<ApiResponse<any>>('/distribution-admin/products', { params }),

  getMembers: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    bot_id?: number;
    sort_by?: 'created_at' | 'commission_rate' | 'gross_commission' | 'order_count';
    sort_order?: 'asc' | 'desc';
  }) => api.get<ApiResponse<any>>('/distribution-admin/members', { params }),

  updateMember: (
    memberId: number,
    data: { is_active?: boolean; custom_commission_rate?: number | null }
  ) => api.patch<ApiResponse<any>>(`/distribution-admin/members/${memberId}`, data),

  getOrders: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    bot_id?: number;
    sort_by?: 'created_at' | 'amount' | 'commission_amount';
    sort_order?: 'asc' | 'desc';
  }) => api.get<ApiResponse<any>>('/distribution-admin/orders', { params }),

  getPayouts: (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
    sort_by?: 'created_at' | 'amount' | 'processed_at';
    sort_order?: 'asc' | 'desc';
  }) => api.get<ApiResponse<any>>('/distribution-admin/payouts', { params }),

  completePayout: (payoutId: number, data?: { processed_note?: string }) =>
    api.post<ApiResponse<any>>(`/distribution-admin/payouts/${payoutId}/complete`, data || {}),

  rejectPayout: (payoutId: number, data: { processed_note: string }) =>
    api.post<ApiResponse<any>>(`/distribution-admin/payouts/${payoutId}/reject`, data),
};

// 支付管理接口
export const paymentApi = {
  getPayments: (params?: {
    status?: string;
    user_id?: number;
    bot_id?: number;
    payment_method?: string;
    skip?: number;
    limit?: number;
    search?: string;
  }) => api.get<ApiResponse<{ total: number; items: any[] }>>('/payments', { params }),

  exportPayments: (params?: {
    status?: string;
    user_id?: number;
    bot_id?: number;
    payment_method?: string;
    search?: string;
  }) =>
    axios.get<Blob>(`${getBaseURL()}/payments/export`, {
      params,
      withCredentials: true,
      responseType: 'blob',
      timeout: 120000,
    }),

  getPayment: (paymentId: string) => api.get<ApiResponse<any>>(`/payments/${paymentId}`),

  getBots: () => api.get<ApiResponse<any[]>>('/payments/bots'),

  syncPayments: (params?: { days?: number; limit?: number }) =>
    api.post<ApiResponse<any>>('/payments/sync', null, { params }),

  quickRefund: (paymentId: string, reason?: string, banUser = false) =>
    api.post<ApiResponse<any>>(`/payments/${paymentId}/refund`, {
      reason,
      ban_user: banUser,
    }),

  generateQRCode: (data: { amount: number; productId: number; userId: number }) =>
    api.post<ApiResponse<{ qrCode: string; orderId: string }>>('/payments/qrcode', data),

  // 支付统计
  getStats: (params?: { start_date?: string; end_date?: string; bot_id?: number }) =>
    api.get<ApiResponse<any>>('/payment-stats/overview', { params }),
};

// 退款记录接口
export const refundApi = {
  getRefunds: (params?: { page?: number; pageSize?: number; status?: string; search?: string }) =>
    api.get<ApiResponse<{ total: number; items: any[] }>>('/payment-refunds', { params }),

  getRefund: (id: number) => api.get<ApiResponse<any>>(`/payment-refunds/${id}`),
};

// 结算支付接口（提现管理）
export const settlementApi = {
  // 获取提现列表
  getWithdrawals: (params?: {
    status?: string;
    request_source?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<
      ApiResponse<{ total: number; items: any[]; limit: number; offset: number; stats?: any }>
    >('/withdrawals/', { params }),

  // 获取提现详情
  getWithdrawal: (id: number) => api.get<ApiResponse<any>>(`/withdrawals/${id}`),

  // 批准提现
  approveWithdrawal: (id: number, admin_note?: string) =>
    api.post<ApiResponse<any>>(`/withdrawals/${id}/approve`, { admin_note }),

  // 拒绝提现
  rejectWithdrawal: (id: number, reject_reason: string) =>
    api.post<ApiResponse<any>>(`/withdrawals/${id}/reject`, { reject_reason }),

  // 执行转账（结算支付核心功能）
  executeTransfer: (id: number, admin_note?: string) =>
    api.post<ApiResponse<any>>(`/withdrawals/${id}/execute`, { admin_note }),

  // 手动完成提现（输入交易哈希）
  completeWithdrawal: (id: number, transaction_id: string, admin_note?: string) =>
    api.post<ApiResponse<any>>(`/withdrawals/${id}/complete`, { transaction_id, admin_note }),

  // 获取提现统计
  getStats: () => api.get<ApiResponse<any>>('/withdrawals/stats/summary'),

  // 获取用户信息
  getUserInfo: (withdrawalId: number) =>
    api.get<ApiResponse<any>>(`/withdrawals/${withdrawalId}/user-info`),
};

// 数据分析接口
export const analyticsApi = {
  getDashboard: () => api.get<ApiResponse<any>>('/analytics/dashboard'),

  getSales: (days: number) => api.get<ApiResponse<any[]>>('/analytics/sales', { params: { days } }),

  getMonthlyRevenue: (months: number) =>
    api.get<ApiResponse<any[]>>('/analytics/monthly-revenue', { params: { months } }),

  getUserGrowth: (days: number) =>
    api.get<ApiResponse<any[]>>('/analytics/users/growth', { params: { days } }),

  getProductStats: (limit?: number) =>
    api.get<ApiResponse<any[]>>('/analytics/products', { params: { limit } }),

  getReferralStats: () => api.get<ApiResponse<any>>('/analytics/referrals'),

  getRecentOrders: (limit?: number) =>
    api.get<ApiResponse<any[]>>('/analytics/recent-orders', { params: { limit } }),

  getRanking: (params?: { period?: string; type?: string; limit?: number }) =>
    api.get<ApiResponse<any[]>>('/analytics/ranking', { params }),

  getChannels: (params?: { days?: number }) =>
    api.get<ApiResponse<any>>('/analytics/channels', { params }),

  getUserBehavior: (params?: {
    time_range?: number;
    min_failed_orders?: number;
    skip?: number;
    limit?: number;
    sort_by?: string;
    order?: string;
  }) => api.get<ApiResponse<any>>('/analytics/user-behavior', { params }),

  getFailureAnalysis: (params?: {
    time_range?: number;
    group_by?: string;
    payment_method?: string;
    product_id?: number;
  }) => api.get<ApiResponse<any>>('/analytics/failure-analysis', { params }),
  getSystemStatus: () => api.get<ApiResponse<any>>('/analytics/system-status'),

  getTrafficOverview: () => api.get<ApiResponse<any>>('/analytics/traffic/overview'),

  getTrafficTrend: (days: number) =>
    api.get<ApiResponse<any[]>>('/analytics/traffic/trend', { params: { days } }),

  getTrafficBots: () => api.get<ApiResponse<any[]>>('/analytics/traffic/bots'),

  searchTraffic: (params: { keyword: string; days?: number; limit?: number }) =>
    api.get<ApiResponse<any>>('/analytics/traffic/search', { params }),
};

// 系统设置接口
export const settingsApi = {
  getSettings: () => api.get<ApiResponse<any>>('/settings'),

  updateSettings: (data: any) => api.put<ApiResponse<any>>('/settings', data),

  getBotSettings: () => api.get<ApiResponse<any>>('/settings/bot'),

  updateBotSettings: (data: any) => api.put<ApiResponse<any>>('/settings/bot', data),

  testBotConnection: () => api.post<ApiResponse<{ status: string }>>('/settings/bot/test'),
};

// 热钱包管理接口
export const walletApi = {
  getBalance: () => api.get<ApiResponse<any>>('/wallet/balance'),

  getTransactions: (params?: { limit?: number; offset?: number }) =>
    api.get<ApiResponse<any[]>>('/wallet/transactions', { params }),

  getStats: (params?: { days?: number }) => api.get<ApiResponse<any>>('/wallet/stats', { params }),

  getTransactionDetail: (txHash: string) =>
    api.get<ApiResponse<any>>(`/wallet/transaction/${txHash}`),
};

// 机器人管理接口
export const botApi = {
  getBots: (
    params?: PaginationParams & { search?: string; user_id?: number; is_active?: boolean }
  ) => api.get<ApiResponse<PaginationResponse<any>>>('/bots', { params }),

  getBot: (id: number) => api.get<ApiResponse<any>>(`/bots/${id}`),

  deleteBot: (id: number) => api.delete<ApiResponse<any>>(`/bots/${id}`),

  reloadBot: (id: number) => api.post<ApiResponse<any>>(`/bots/${id}/reload`),

  repairMigration: (data: { source_bot_username: string; target_bot_username: string }) =>
    api.post<ApiResponse<any>>('/bots/migration-repair', data),

  freezeWithdrawal: (id: number) => api.post<ApiResponse<any>>(`/bots/${id}/freeze-withdrawal`),

  unfreezeWithdrawal: (id: number) => api.post<ApiResponse<any>>(`/bots/${id}/unfreeze-withdrawal`),
};

// 群聊监控接口
export const groupMonitorApi = {
  getGroupsMonitor: (params?: {
    group_id?: number;
    page?: number;
    pageSize?: number;
    search?: string;
  }) =>
    api.get<ApiResponse<{ total: number; items: any[] }>>('/group-monitor/groups-monitor', {
      params,
    }),

  getStats: () => api.get<ApiResponse<any>>('/group-monitor/group-monitor-stats'),
};

// 会员手动操作接口
export const memberActionsApi = {
  kickMember: (data: { membership_id: number; reason?: string }) =>
    api.post<ApiResponse<any>>('/member-actions/kick-member', data),

  restoreMember: (data: { membership_id: number; reason?: string }) =>
    api.post<ApiResponse<any>>('/member-actions/restore-member', data),

  extendMembership: (data: { membership_id: number; extend_days: number }) =>
    api.post<ApiResponse<any>>('/member-actions/extend-membership', data),

  suspendMember: (data: { membership_id: number; reason?: string }) =>
    api.post<ApiResponse<any>>('/member-actions/suspend-member', data),
};

// 补单管理接口
export const retryOrderApi = {
  retry: (data: { order_number: string; force?: boolean; actual_amount?: number }) =>
    api.post('/retry-order/retry', data),

  resendInvite: (data: { order_number: string }) => api.post('/retry-order/resend-invite', data),

  getPendingOrders: (limit?: number) =>
    api.get('/retry-order/pending-orders', { params: { limit } }),
};

// 服务器管理接口
export const serverControlApi = {
  getStatus: () => api.get<ApiResponse<any>>('/server/status'),

  getVpsInfo: () => api.get<ApiResponse<any>>('/server/vps-info'),

  restartService: (serviceName: string) =>
    api.post<ApiResponse<any>>(`/server/restart-service/${serviceName}`),

  restartAllServices: () => api.post<ApiResponse<any>>('/server/restart-all-services'),

  restartVps: () => api.post<ApiResponse<any>>('/server/restart-vps'),

  ping: () => api.get('/server/ping'),

  // 主Bot Token管理
  getBotInfo: () => api.get('/server/bot-info'),

  testBotToken: (token: string) => api.post('/server/test-bot-token', { token }),

  updateBotToken: (token: string) => api.post('/server/update-bot-token', { token }),

  // 提现审核阈值
  getWithdrawalReviewThreshold: () => api.get('/server/withdrawal-review-threshold'),

  setWithdrawalReviewThreshold: (threshold: number) =>
    api.post('/server/withdrawal-review-threshold', { threshold }),

  getWithdrawalMaintenance: () => api.get('/server/withdrawal-maintenance'),

  setWithdrawalMaintenance: (enabled: boolean) =>
    api.post('/server/withdrawal-maintenance', { enabled }),

  // 推荐支付方式
  getRecommendedPayment: () => api.get('/server/recommended-payment'),

  setRecommendedPayment: (methodId: string) =>
    api.post('/server/recommended-payment', { method_id: methodId }),

  // 支付通道权重
  getPaymentWeights: () => api.get('/server/payment-weights'),

  setPaymentWeights: (weights: Record<string, number>) =>
    api.post('/server/payment-weights', { weights }),

  getAiSupportConfig: () => api.get<AiSupportConfig>('/server/ai-support-config'),

  updateAiSupportConfig: (data: AiSupportConfig) =>
    api.put<AiSupportConfig>('/server/ai-support-config', data),

  testAiProvider: (data: Partial<AiSupportConfig>) => api.post('/server/test-ai-provider', data),
};

// 客服消息接口
export const customerServiceApi = {
  getConversations: () => api.get<ApiResponse<any[]>>('/customer-service/conversations'),

  getMessages: (customerTelegramId: number, limit?: number) =>
    api.get<ApiResponse<any[]>>('/customer-service/messages', {
      params: { customer_telegram_id: customerTelegramId, limit },
    }),

  reply: (data: { customer_telegram_id: number; content: string }) =>
    api.post<ApiResponse<any>>('/customer-service/reply', data),

  replyPhoto: (customerTelegramId: number, file: File, caption?: string) => {
    const formData = new FormData();
    formData.append('customer_telegram_id', String(customerTelegramId));
    formData.append('photo', file);
    if (caption) formData.append('caption', caption);
    return api.post<ApiResponse<any>>('/customer-service/reply-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getKbDocuments: (params?: { keyword?: string; doc_type?: string; is_active?: boolean }) =>
    api.get<KbDocument[]>('/customer-service/kb/documents', { params }),

  createKbDocument: (data: Omit<KbDocument, 'id' | 'created_at' | 'updated_at' | 'chunk_count'>) =>
    api.post<{ id: number }>('/customer-service/kb/documents', data),

  updateKbDocument: (
    id: number,
    data: Omit<KbDocument, 'id' | 'created_at' | 'updated_at' | 'chunk_count'>
  ) => api.put<{ id: number }>(`/customer-service/kb/documents/${id}`, data),

  toggleKbDocument: (id: number, isActive: boolean) =>
    api.post<{ id: number; is_active: boolean }>(`/customer-service/kb/documents/${id}/toggle`, {
      is_active: isActive,
    }),

  deleteKbDocument: (id: number) =>
    api.delete<{ id: number }>(`/customer-service/kb/documents/${id}`),

  previewAiReply: (data: { message_text: string; customer_telegram_id?: number }) =>
    api.post<AiPreviewResult>('/customer-service/ai/preview', data),

  getAiLogs: (params?: { customer_telegram_id?: number; limit?: number }) =>
    api.get<AiReplyLog[]>('/customer-service/ai/logs', { params }),
};

export default api;
